import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "../styles/share-access-code.css";

const ShareAccessCode = () => {

  const API_BASE_URL = "https://api.omegavoid.com";

  const [userHandle, setUserHandle] = useState('');
  const [newHandle, setNewHandle] = useState('');
  const [friendsList, setFriendsList] = useState([]);
  const [blockedList, setBlockedList] = useState([]);
  const [showEditHandle, setShowEditHandle] = useState(false);
  const [editingFriendId, setEditingFriendId] = useState(null);
  const [editingFriendHandle, setEditingFriendHandle] = useState('');
  const [editingFriendActualHandle, setEditingFriendActualHandle] = useState('');
  const [newFriendHandle, setNewFriendHandle] = useState('');
  const [searchHandle, setSearchHandle] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const [selectedFriend, setSelectedFriend] = useState(null);
  const [conversationMessages, setConversationMessages] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('friends');
  const [loadingState, setLoadingState] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [omegaLinkInput, setOmegaLinkInput] = useState('');
  const [linkNote, setLinkNote] = useState('');

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  // iOS Safari push requires the site to be installed to the Home Screen (iOS 16.4+)
  // and launched from there - detect so we can show a tailored install prompt.
  const isIOS = typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
     window.navigator.standalone === true);

  const [messageCache, setMessageCache] = useState({});
  const [unreadPerFriend, setUnreadPerFriend] = useState({});
  const messageLoadTimestampsRef = useRef({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    document.title = 'Omega Inbox - OmegaVoid';
    return () => { document.title = 'OmegaVoid'; };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsLoadingData(false);
      return;
    }

    setAuthToken(token);
    setIsAuthenticated(true);
    loadUserData(token);
  }, []);

  // Detect Web Push support + current subscription state
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
    setPushSupported(supported);
    if (!supported) return;

    navigator.serviceWorker.getRegistration('/sw.js').then((reg) => {
      if (!reg) return;
      reg.pushManager.getSubscription().then((sub) => setPushSubscribed(!!sub));
    }).catch(() => {});
  }, []);

  // Poll for per-friend unread counts
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const fetchUnreadCounts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/omega-links/unread-count`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUnreadPerFriend(data.per_sender || {});
        }
      } catch (error) {
        console.debug('Unread count poll failed:', error);
      }
    };

    fetchUnreadCounts();
    const intervalId = setInterval(fetchUnreadCounts, 45000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, authToken]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  useEffect(() => {
    const messagesArea = document.querySelector('.messages-area');
    if (messagesArea) {
      setTimeout(() => {
        messagesArea.scrollTop = messagesArea.scrollHeight;
      }, 0);
    }
  }, [conversationMessages, selectedFriend]);

  const showNotification = (message, isError = false) => {
    if (isError) {
      setErrorMessage(message);
      setSuccessMessage('');
    } else {
      setSuccessMessage(message);
      setErrorMessage('');
    }
  };

  const loadUserData = async (token) => {
    try {
      setIsLoadingData(true);

      const handlerResponse = await fetch(`${API_BASE_URL}/handlers/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (handlerResponse.ok) {
        const handlerData = await handlerResponse.json();
        setUserHandle(handlerData.messaging_handle || '');
      } else if (handlerResponse.status === 404) {
        setUserHandle('');
      }

      const friendsResponse = await fetch(`${API_BASE_URL}/friends`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (friendsResponse.ok) {
        const friendsData = await friendsResponse.json();
        const formattedFriends = (friendsData.friends || [])
          .filter(f => !f.is_blocked)
          .map(f => ({
            id: f.friend_payment_hash,
            handle: f.friend_handle_actual,
            addedDate: new Date(f.added_at).toLocaleDateString(),
            isBlocked: f.is_blocked || false,
            sharedCodesCount: f.shared_codes_count || 0
          }));
        setFriendsList(formattedFriends);
      }

      const blockedResponse = await fetch(`${API_BASE_URL}/blocked`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (blockedResponse.ok) {
        const blockedData = await blockedResponse.json();
        const formattedBlocked = (blockedData.blocked || []).map(b => ({
          id: b.friend_payment_hash,
          handle: b.friend_handle_actual,
          blockedDate: new Date(b.added_at).toLocaleDateString()
        }));
        setBlockedList(formattedBlocked);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.error('Error loading user data:', error);
      showNotification('Error loading data. Please refresh.', true);
      setIsLoadingData(false);
    }
  };

  const deduplicateAndFilterMessages = useCallback((receivedLinks, friendId) => {
    const seen = new Set();
    const deduplicatedMessages = [];

    if (Array.isArray(receivedLinks)) {
      receivedLinks.forEach((link) => {
        if (link.sender_hash === friendId) {
          const messageKey = `received-${link.link_id}`;
          if (!seen.has(messageKey)) {
            seen.add(messageKey);
            deduplicatedMessages.push({
              id: link.link_id,
              type: 'received',
              sender: link.sent_from,
              senderHash: link.sender_hash,
              text: link.omega_link,
              note: link.message,
              timestamp: link.sent_at,
              date: link.sent_at
            });
          }
        }
      });
    }

    const storedConversation = localStorage.getItem(`conversation_${friendId}`);
    if (storedConversation) {
      const sentMessages = JSON.parse(storedConversation);
      sentMessages.forEach(msg => {
        const messageKey = `sent-${msg.id}`;
        if (!seen.has(messageKey)) {
          seen.add(messageKey);
          deduplicatedMessages.push({
            id: msg.id,
            type: 'sent',
            recipient: friendId,
            text: msg.text,
            note: msg.note,
            timestamp: msg.timestamp,
            date: msg.date
          });
        }
      });
    }

    return deduplicatedMessages.sort((a, b) =>
      new Date(a.timestamp) - new Date(b.timestamp)
    );
  }, []);

  const loadConversation = async (friendId, friendHash) => {
    try {
      const cacheKey = friendId;
      const lastLoadTime = messageLoadTimestampsRef.current[cacheKey];

      if (lastLoadTime && Date.now() - lastLoadTime < 2000) {
        if (messageCache[cacheKey]) {
          setConversationMessages({
            ...conversationMessages,
            [friendId]: messageCache[cacheKey]
          });
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/omega-links/received`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const filteredMessages = deduplicateAndFilterMessages(data.links || [], friendHash);

        messageLoadTimestampsRef.current[cacheKey] = Date.now();
        setMessageCache(prev => ({
          ...prev,
          [cacheKey]: filteredMessages
        }));

        setConversationMessages({
          ...conversationMessages,
          [friendId]: filteredMessages
        });

        // Delete-after-read: mark received messages as read (deletes from server DB)
        const receivedLinks = (data.links || []).filter(l => l.sender_hash === friendHash);
        for (const link of receivedLinks) {
          markLinkAsRead(link.link_id, link.sender_hash);
        }

        // Clear unread count for this friend locally
        setUnreadPerFriend(prev => {
          const updated = { ...prev };
          delete updated[friendHash];
          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleSendLink = async () => {
    if (!selectedFriend) {
      showNotification('Please select a friend', true);
      return;
    }

    if (!omegaLinkInput.trim()) {
      showNotification('Please paste an Omega Link', true);
      return;
    }

    if (!omegaLinkInput.includes('omega://')) {
      showNotification('Invalid Omega Link format', true);
      return;
    }

    try {
      setLoadingState({ ...loadingState, sendLink: true });

      const response = await fetch(`${API_BASE_URL}/omega-links/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: selectedFriend.handle,
          omega_link: omegaLinkInput,
          message: linkNote
        })
      });

      const data = await response.json();

      if (response.ok) {
        const newMessage = {
          id: Date.now().toString(),
          type: 'sent',
          recipient: selectedFriend.id,
          text: omegaLinkInput,
          note: linkNote,
          timestamp: new Date().toISOString(),
          date: new Date().toLocaleDateString()
        };

        const updatedMessages = conversationMessages[selectedFriend.id] || [];
        updatedMessages.push(newMessage);

        setConversationMessages({
          ...conversationMessages,
          [selectedFriend.id]: updatedMessages
        });

        messageLoadTimestampsRef.current[selectedFriend.id] = 0;
        setMessageCache(prev => ({
          ...prev,
          [selectedFriend.id]: undefined
        }));

        localStorage.setItem(
          `conversation_${selectedFriend.id}`,
          JSON.stringify(updatedMessages.filter(m => m.type === 'sent'))
        );

        const updatedFriends = friendsList.map(f => {
          if (f.id === selectedFriend.id) {
            return { ...f, sharedCodesCount: (f.sharedCodesCount || 0) + 1 };
          }
          return f;
        });
        setFriendsList(updatedFriends);

        setOmegaLinkInput('');
        setLinkNote('');

        showNotification(`Link sent to ${selectedFriend.handle}`);
      } else {
        showNotification(data.error || 'Error sending link', true);
      }
    } catch (error) {
      console.error('Error sending link:', error);
      showNotification('Error sending link. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, sendLink: false });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Copied to clipboard!');
    }).catch(() => {
      showNotification('Copy failed', true);
    });
  };

  const handleDeleteMessage = async (messageId, friendId, messageType, senderHash) => {
    if (!window.confirm('Delete this message?')) return;

    // If it's a received message, delete from backend too
    if (messageType === 'received') {
      try {
        await fetch(`${API_BASE_URL}/omega-links`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            link_id: messageId,
            sender_hash: senderHash || ''
          })
        });
      } catch (error) {
        console.error('Error deleting from server:', error);
      }
    }

    const updatedMessages = conversationMessages[friendId]?.filter(m => m.id !== messageId) || [];
    setConversationMessages({
      ...conversationMessages,
      [friendId]: updatedMessages
    });

    messageLoadTimestampsRef.current[friendId] = 0;

    localStorage.setItem(
      `conversation_${friendId}`,
      JSON.stringify(updatedMessages.filter(m => m.type === 'sent'))
    );

    showNotification('Message deleted');
  };

  const markLinkAsRead = async (linkId, senderHash) => {
    /** Delete-after-read: notify backend to remove link from DB after recipient reads it */
    try {
      await fetch(`${API_BASE_URL}/omega-links/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          link_id: linkId,
          sender_hash: senderHash || ''
        })
      });
    } catch (error) {
      console.error('Error marking link as read:', error);
    }
  };

  // Base64URL → Uint8Array (VAPID public key needs to be a Uint8Array for PushManager.subscribe)
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
    return output;
  };

  const handleEnablePush = async () => {
    if (!pushSupported) {
      showNotification('Push notifications are not supported on this browser', true);
      return;
    }
    try {
      setPushBusy(true);

      const perm = await Notification.requestPermission();
      setPushPermission(perm);
      if (perm !== 'granted') {
        showNotification('Notification permission denied', true);
        return;
      }

      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Fetch VAPID public key from the API (no auth required for this)
      const keyResp = await fetch(`${API_BASE_URL}/push/public-key`);
      if (!keyResp.ok) throw new Error('Could not load push key');
      const { public_key: vapidPublicKey } = await keyResp.json();

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      const subJson = subscription.toJSON();
      const resp = await fetch(`${API_BASE_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys
        })
      });

      if (!resp.ok) throw new Error('Server rejected subscription');
      setPushSubscribed(true);
      showNotification('Notifications enabled');
    } catch (err) {
      console.error('Enable push failed:', err);
      showNotification(err.message || 'Could not enable notifications', true);
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    try {
      setPushBusy(true);
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg && (await reg.pushManager.getSubscription());
      const endpoint = sub && sub.endpoint;

      if (sub) await sub.unsubscribe();

      if (endpoint) {
        await fetch(`${API_BASE_URL}/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ endpoint })
        });
      }
      setPushSubscribed(false);
      showNotification('Notifications disabled');
    } catch (err) {
      console.error('Disable push failed:', err);
      showNotification(err.message || 'Could not disable notifications', true);
    } finally {
      setPushBusy(false);
    }
  };

  const handleUpdateHandle = async () => {
    if (!newHandle.trim()) {
      showNotification('Please enter a handle', true);
      return;
    }
    if (newHandle.length < 3 || newHandle.length > 20) {
      showNotification('Handle must be 3-20 characters', true);
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(newHandle)) {
      showNotification('Only letters, numbers, _, - allowed', true);
      return;
    }

    try {
      setLoadingState({ ...loadingState, handle: true });

      const response = await fetch(`${API_BASE_URL}/handlers/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messaging_handle: newHandle.toLowerCase()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserHandle(data.messaging_handle || newHandle.toLowerCase());
        setNewHandle('');
        setShowEditHandle(false);
        showNotification('Handle updated!');
      } else if (response.status === 409) {
        showNotification('Handle already taken', true);
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error updating handle', true);
      }
    } catch (error) {
      console.error('Error updating handle:', error);
      showNotification('Error updating handle. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, handle: false });
    }
  };

  const handleSearchHandle = async () => {
    const cleanHandle = searchHandle.trim().replace(/^@/, '');
    if (!cleanHandle) {
      showNotification('Enter a handle to search', true);
      return;
    }

    try {
      setLoadingState({ ...loadingState, search: true });

      const response = await fetch(`${API_BASE_URL}/handlers/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          handle: cleanHandle.toLowerCase()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults({
          handle: data.handle,
          name: data.name || data.handle
        });
      } else if (response.status === 404) {
        showNotification('Not found', true);
        setSearchResults(null);
      } else {
        showNotification('Error searching', true);
        setSearchResults(null);
      }
    } catch (error) {
      console.error('Error searching handle:', error);
      showNotification('Error searching. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, search: false });
    }
  };

  const handleAddFriend = async (handleOverride) => {
    const sourceHandle = typeof handleOverride === 'string' ? handleOverride : newFriendHandle;
    const handle = (sourceHandle || '').trim().replace(/^@/, '');
    if (!handle) {
      showNotification('Enter friend handle', true);
      return;
    }
    if (handle.length < 3 || handle.length > 20) {
      showNotification('Handle must be 3-20 characters', true);
      return;
    }
    if (handle.toLowerCase() === (userHandle || '').toLowerCase()) {
      showNotification('Cannot add yourself', true);
      return;
    }
    if (friendsList.some(f => f.handle === handle.toLowerCase())) {
      showNotification('Friend is already added', true);
      return;
    }

    try {
      setLoadingState({ ...loadingState, addFriend: true });

      const response = await fetch(`${API_BASE_URL}/friends`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: handle.toLowerCase()
        })
      });

      if (response.ok) {
        const data = await response.json();
        const newFriend = {
          id: data.friend.friend_payment_hash || Date.now(),
          handle: handle.toLowerCase(),
          addedDate: new Date().toLocaleDateString(),
          isBlocked: false,
          sharedCodesCount: 0
        };

        setFriendsList([...friendsList, newFriend]);
        setNewFriendHandle('');
        setSearchResults(null);
        setSearchHandle('');
        showNotification(`Added ${handle.toLowerCase()}`);
      } else if (response.status === 404) {
        showNotification('Not found', true);
      } else if (response.status === 409) {
        showNotification('Friend is already added', true);
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error adding friend', true);
      }
    } catch (error) {
      console.error('Error adding friend:', error);
      showNotification('Error adding friend. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, addFriend: false });
    }
  };

  const handleRemoveFriend = async (friendId, friendHandle) => {
    if (!window.confirm(`Remove ${friendHandle}?`)) return;

    try {
      setLoadingState({ ...loadingState, removeFriend: friendId });

      const response = await fetch(`${API_BASE_URL}/friends`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: friendHandle
        })
      });

      if (response.ok) {
        setFriendsList(friendsList.filter(f => f.id !== friendId));
        if (selectedFriend?.id === friendId) {
          setSelectedFriend(null);
        }
        showNotification(`Removed ${friendHandle}`);
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error removing friend', true);
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      showNotification('Error removing friend. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, removeFriend: null });
    }
  };

  const handleEditFriend = (friendId, currentHandle) => {
    setEditingFriendId(friendId);
    setEditingFriendHandle(currentHandle);
    setEditingFriendActualHandle(currentHandle);
  };

  const handleSaveEditFriend = async (friendId) => {
    if (!editingFriendHandle.trim()) {
      showNotification('Handle cannot be empty', true);
      return;
    }
    if (editingFriendHandle.length < 3 || editingFriendHandle.length > 20) {
      showNotification('Handle must be 3-20 characters', true);
      return;
    }

    try {
      setLoadingState({ ...loadingState, editFriend: true });

      const response = await fetch(`${API_BASE_URL}/friends`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: editingFriendActualHandle,
          new_handle: editingFriendHandle.toLowerCase()
        })
      });

      if (response.ok) {
        const newHandle = editingFriendHandle.toLowerCase();
        const updated = friendsList.map(f =>
          f.id === friendId ? { ...f, handle: newHandle } : f
        );
        setFriendsList(updated);
        if (selectedFriend?.id === friendId) {
          setSelectedFriend({ ...selectedFriend, handle: newHandle });
        }
        setEditingFriendId(null);
        setEditingFriendHandle('');
        setEditingFriendActualHandle('');
        showNotification('Handle updated!');
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error updating handle', true);
      }
    } catch (error) {
      console.error('Error updating friend handle:', error);
      showNotification('Error updating handle. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, editFriend: false });
    }
  };

  const handleBlockFriend = async (friendId, friendHandle) => {
    if (!window.confirm(`Block ${friendHandle}?`)) return;

    try {
      setLoadingState({ ...loadingState, blockFriend: friendId });

      const response = await fetch(`${API_BASE_URL}/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: friendHandle
        })
      });

      if (response.ok) {
        const friend = friendsList.find(f => f.id === friendId);
        if (friend) {
          setBlockedList([...blockedList, { ...friend, blockedDate: new Date().toLocaleDateString() }]);
          setFriendsList(friendsList.filter(f => f.id !== friendId));
          if (selectedFriend?.id === friendId) {
            setSelectedFriend(null);
          }
          showNotification(`Blocked ${friendHandle}`);
        }
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error blocking friend', true);
      }
    } catch (error) {
      console.error('Error blocking friend:', error);
      showNotification('Error blocking friend. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, blockFriend: null });
    }
  };

  const handleUnblockFriend = async (blockedId, blockedHandle) => {
    if (!window.confirm(`Unblock ${blockedHandle}?`)) return;

    try {
      setLoadingState({ ...loadingState, unblockFriend: blockedId });

      const response = await fetch(`${API_BASE_URL}/unblock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_handle: blockedHandle
        })
      });

      if (response.ok) {
        const unblockedUser = blockedList.find(b => b.id === blockedId);
        setBlockedList(blockedList.filter(b => b.id !== blockedId));
        if (unblockedUser) {
          setFriendsList(prev => [...prev, {
            id: unblockedUser.id,
            handle: unblockedUser.handle,
            addedDate: unblockedUser.blockedDate,
            isBlocked: false,
            sharedCodesCount: 0
          }]);
        }
        showNotification(`Unblocked ${blockedHandle}`);
      } else {
        const error = await response.json();
        showNotification(error.message || 'Error unblocking friend', true);
      }
    } catch (error) {
      console.error('Error unblocking friend:', error);
      showNotification('Error unblocking friend. Please try again.', true);
    } finally {
      setLoadingState({ ...loadingState, unblockFriend: null });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="share-access-wrapper">
        <div className="share-access-container">
          <div className="share-access-inner">
            <div className="share-tabs">
              <button className="share-tab active">
                <span className="tab-icon">💬</span>
                <span className="tab-text">Messages</span>
              </button>
              <button className="share-tab">
                <span className="tab-icon">🚫</span>
                <span className="tab-text">Blocked (0)</span>
              </button>
              <button className="share-tab">
                <span className="tab-icon">⚙️</span>
                <span className="tab-text">Settings</span>
              </button>
            </div>
            <div className="share-content">
              <div className="chat-interface">
                <div className="friends-panel" style={{ width: '100%', borderRight: 'none' }}>
                  <div className="friends-header">
                    <h3>Inbox</h3>
                  </div>
                  <div className="friends-empty" style={{ height: 'calc(100% - 60px)' }}>
                    <div className="empty-illustration">📬</div>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '1.3rem', margin: '0 0 8px 0' }}>Your Inbox is Empty</h3>
                    <p style={{ marginBottom: '20px' }}>Sign in to share encrypted Omega Links with friends</p>
                    <Link to="/login" className="btn btn-accent">
                      Sign In to Continue
                    </Link>
                    <p style={{ fontSize: '0.8rem', marginTop: '16px', color: 'var(--text-muted)' }}>
                      Don't have an account? <Link to="/mobile" style={{ color: 'var(--accent)' }}>Create one</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="share-access-wrapper">
        <div className="share-access-container">
          <div className="empty-state" style={{ padding: '100px 30px' }}>
            <div className="loading-spinner"></div>
            <h3>Loading...</h3>
            <p>Please wait while we fetch your data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="share-access-wrapper">
      {(successMessage || errorMessage) && (
        <div className={`notification ${successMessage ? 'success' : 'error'}`}>
          {successMessage || errorMessage}
        </div>
      )}

      <div className="share-access-container">
        <div className="share-access-inner">
          <div className="share-tabs">
            <button
              className={`share-tab ${activeTab === 'friends' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('friends');
                if (isMobile) setSelectedFriend(null);
              }}
            >
              <span className="tab-icon">💬</span>
              <span className="tab-text">Messages</span>
            </button>
            <button
              className={`share-tab ${activeTab === 'blocked' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('blocked');
                if (isMobile) setSelectedFriend(null);
              }}
            >
              <span className="tab-icon">🚫</span>
              <span className="tab-text">Blocked ({blockedList.length})</span>
            </button>
            <button
              className={`share-tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('settings');
                if (isMobile) setSelectedFriend(null);
              }}
            >
              <span className="tab-icon">⚙️</span>
              <span className="tab-text">Settings</span>
            </button>
          </div>

          <div className="share-content">
            {activeTab === 'friends' && (
              <div className="chat-interface">
                {(!selectedFriend || !isMobile) && (
                  <div className="friends-panel">
                    <div className="friends-header">
                      <h3>Chats</h3>
                      <button
                        className="btn btn-sm btn-accent"
                        onClick={() => setActiveTab('settings')}
                        title="Add friend"
                      >
                        + Add
                      </button>
                    </div>

                    {friendsList.length === 0 ? (
                      <div className="friends-empty">
                        <div className="empty-illustration">👥</div>
                        <p>No friends yet</p>
                        <button
                          className="btn btn-accent btn-sm"
                          onClick={() => setActiveTab('settings')}
                        >
                          Add Friends
                        </button>
                      </div>
                    ) : (
                      <div className="friends-chat-list">
                        {friendsList.map((friend, index) => (
                          <div
                            key={friend.id}
                            className={`chat-item ${selectedFriend?.id === friend.id ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedFriend(friend);
                              loadConversation(friend.id, friend.id);
                            }}
                            style={{ animationDelay: `${index * 0.05}s` }}
                          >
                            <div className="chat-avatar" data-letter={friend.handle[0]?.toUpperCase()}>
                              {friend.handle[0]?.toUpperCase()}
                            </div>
                            <div className="chat-info">
                              <div className="chat-name">{friend.handle}</div>
                              <div className="chat-preview">
                                {unreadPerFriend[friend.id]
                                  ? `${unreadPerFriend[friend.id]} new message${unreadPerFriend[friend.id] > 1 ? 's' : ''}`
                                  : 'Tap to open chat'}
                              </div>
                            </div>
                            {unreadPerFriend[friend.id] > 0 && (
                              <span className="unread-badge">
                                {unreadPerFriend[friend.id]}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(selectedFriend || !isMobile) && (
                  <div className="chat-window" style={isMobile && selectedFriend ? { visibility: 'visible', opacity: 1 } : {}}>
                    {!selectedFriend ? (
                      <div className="chat-empty-state">
                        <div className="empty-chat-illustration">
                          <span>💬</span>
                        </div>
                        <h3>Select a chat</h3>
                        <p>Choose a friend to start sharing links</p>
                      </div>
                    ) : (
                      <>
                        <div className="chat-top-header">
                          <div className="header-left">
                            {isMobile && (
                              <button
                                className="back-btn"
                                onClick={() => setSelectedFriend(null)}
                                title="Back"
                              >
                                ←
                              </button>
                            )}
                            <div className="header-avatar">
                              {selectedFriend.handle[0]?.toUpperCase()}
                            </div>
                            <div className="header-info">
                              <h3>@{selectedFriend.handle}</h3>
                            </div>
                          </div>
                          <div className="header-actions">
                            <button
                              className="btn-icon"
                              onClick={() => handleEditFriend(selectedFriend.id, selectedFriend.handle)}
                              title="Edit Handle"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleBlockFriend(selectedFriend.id, selectedFriend.handle)}
                              disabled={loadingState.blockFriend === selectedFriend.id}
                              title="Block"
                            >
                              🚫
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => handleRemoveFriend(selectedFriend.id, selectedFriend.handle)}
                              disabled={loadingState.removeFriend === selectedFriend.id}
                              title="Remove"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>

                        {editingFriendId === selectedFriend.id && (
                          <div className="edit-friend-modal">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h4>Edit Handle</h4>
                              </div>
                              <input
                                type="text"
                                value={editingFriendHandle}
                                onChange={(e) => setEditingFriendHandle(e.target.value.toLowerCase())}
                                maxLength={20}
                                className="input-field"
                                autoFocus
                                placeholder="Enter new handle"
                              />
                              <div className="button-group">
                                <button
                                  className="btn btn-accent"
                                  onClick={() => handleSaveEditFriend(selectedFriend.id)}
                                  disabled={loadingState.editFriend}
                                >
                                  {loadingState.editFriend ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  className="btn btn-ghost"
                                  onClick={() => setEditingFriendId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="messages-area">
                          {(!conversationMessages[selectedFriend.id] || conversationMessages[selectedFriend.id].length === 0) ? (
                            <div className="messages-empty">
                              <div className="empty-messages-icon">📨</div>
                              <p>No messages yet</p>
                              <p className="hint">Send your first Omega Link below</p>
                            </div>
                          ) : (
                            <div className="messages-container">
                              {conversationMessages[selectedFriend.id]?.map((msg, index) => (
                                <div
                                  key={msg.id}
                                  className={`message ${msg.type}`}
                                  style={{ animationDelay: `${index * 0.03}s` }}
                                >
                                  <div className="message-bubble">
                                    <div className="link-code">{msg.text}</div>
                                    {msg.note && <div className="message-note">{msg.note}</div>}
                                    <div className="message-time">{new Date(msg.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                                  </div>
                                  <div className="message-actions">
                                    <button
                                      className="msg-action-btn"
                                      onClick={() => copyToClipboard(msg.text)}
                                      title="Copy"
                                    >
                                      📋
                                    </button>
                                    <button
                                      className="msg-action-btn delete"
                                      onClick={() => handleDeleteMessage(msg.id, selectedFriend.id, msg.type, msg.senderHash)}
                                      title="Delete"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <div ref={messagesEndRef} />
                            </div>
                          )}
                        </div>

                        <div className="chat-input-area">
                          <div className="input-group">
                            <textarea
                              placeholder="Paste Omega Link here..."
                              value={omegaLinkInput}
                              onChange={(e) => setOmegaLinkInput(e.target.value)}
                              maxLength={500}
                              className="chat-textarea"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  handleSendLink();
                                }
                              }}
                            />
                            <button
                              className="send-btn"
                              onClick={handleSendLink}
                              disabled={loadingState.sendLink || !omegaLinkInput.trim()}
                              title="Send (Ctrl+Enter)"
                            >
                              {loadingState.sendLink ? (
                                <span className="btn-spinner"></span>
                              ) : (
                                <span>➤</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="share-tab-content">
                <div className="settings-section">
                  <div className="section-header">
                    <span className="section-icon">👤</span>
                    <h2>Your Handle</h2>
                  </div>
                  <div className="handle-display-box">
                    <div
                      className="handle-display"
                      onClick={() => userHandle && copyToClipboard(userHandle)}
                      style={userHandle ? { cursor: 'pointer' } : {}}
                      title={userHandle ? 'Click to copy' : ''}
                    >
                      {userHandle ? `@${userHandle}` : 'Not set'}
                    </div>
                    {!showEditHandle ? (
                      <button className="btn btn-accent" onClick={() => setShowEditHandle(true)}>
                        Edit Handle
                      </button>
                    ) : (
                      <div className="edit-form">
                        <input
                          type="text"
                          placeholder="Enter new handle"
                          value={newHandle}
                          onChange={(e) => setNewHandle(e.target.value.toLowerCase())}
                          maxLength={20}
                          className="input-field"
                          autoFocus
                        />
                        <div className="form-hint">3-20 characters, letters/numbers/_/-</div>
                        <div className="button-group">
                          <button
                            className="btn btn-accent"
                            onClick={handleUpdateHandle}
                            disabled={loadingState.handle}
                          >
                            {loadingState.handle ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-ghost"
                            onClick={() => {
                              setShowEditHandle(false);
                              setNewHandle('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="settings-section">
                  <div className="section-header">
                    <span className="section-icon">🔔</span>
                    <h2>Notifications</h2>
                  </div>
                  <div className="handle-display-box">
                    {!pushSupported && isIOS && !isStandalone ? (
                      <div style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                        <strong>iPhone / iPad setup needed</strong><br />
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                          Apple only allows push notifications on iOS 16.4+ when the site is installed to your Home Screen.
                        </span>
                        <ol style={{ marginTop: '12px', paddingLeft: '20px', lineHeight: '1.6' }}>
                          <li>Tap the <strong>Share</strong> button in Safari (square with upward arrow at the bottom of the screen)</li>
                          <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                          <li>Tap <strong>Add</strong> in the top right</li>
                          <li>Open <strong>Omega Void</strong> from the new Home Screen icon</li>
                          <li>Come back here to <strong>Settings → Notifications</strong> and tap Enable</li>
                        </ol>
                      </div>
                    ) : !pushSupported && isIOS && isStandalone ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Push notifications need iOS 16.4 or later. Please update your iPhone / iPad in Settings → General → Software Update.
                      </div>
                    ) : !pushSupported ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Push notifications aren't supported on this browser. Try the latest Chrome, Firefox, Edge, or Safari.
                      </div>
                    ) : pushPermission === 'denied' ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        You've blocked notifications for omegavoid.com. Re-enable them in your browser's site settings, then reload.
                      </div>
                    ) : pushSubscribed ? (
                      <>
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Enabled</strong> — you'll get pinged when a friend sends you an Omega Link, even if the tab is closed.
                          <br />
                          <small style={{ color: 'var(--text-muted)' }}>
                            Notifications never contain the sender or message content.
                          </small>
                        </div>
                        <button
                          className="btn btn-ghost"
                          onClick={handleDisablePush}
                          disabled={pushBusy}
                        >
                          {pushBusy ? '...' : 'Disable notifications'}
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ marginBottom: '10px' }}>
                          Get pinged when a friend sends you an Omega Link — even when the tab is closed.
                          <br />
                          <small style={{ color: 'var(--text-muted)' }}>
                            Notifications never contain the sender or message content.
                          </small>
                        </div>
                        <button
                          className="btn btn-accent"
                          onClick={handleEnablePush}
                          disabled={pushBusy}
                        >
                          {pushBusy ? '...' : '🔔 Enable notifications'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="settings-section">
                  <div className="section-header">
                    <span className="section-icon">🔍</span>
                    <h2>Search & Add Friend</h2>
                  </div>
                  <div className="search-friend-form">
                    <div className="search-input-group">
                      <input
                        type="text"
                        placeholder="Search by handle..."
                        value={searchHandle}
                        onChange={(e) => setSearchHandle(e.target.value.toLowerCase())}
                        maxLength={20}
                        className="input-field"
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchHandle()}
                      />
                      <button
                        className="btn btn-accent"
                        onClick={handleSearchHandle}
                        disabled={loadingState.search}
                      >
                        {loadingState.search ? '...' : 'Search'}
                      </button>
                    </div>

                    {searchResults && (
                      <div className="search-result">
                        <div className="result-avatar">
                          {searchResults.handle[0]?.toUpperCase()}
                        </div>
                        <div className="result-info">
                          <h4>@{searchResults.handle}</h4>
                          <p>{searchResults.name}</p>
                        </div>
                        <button
                          className="btn btn-accent btn-sm"
                          onClick={() => handleAddFriend(searchResults.handle)}
                          disabled={loadingState.addFriend}
                        >
                          {loadingState.addFriend ? '...' : 'Add'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="add-friend-form">
                    <input
                      type="text"
                      placeholder="Enter friend's handle"
                      value={newFriendHandle}
                      onChange={(e) => setNewFriendHandle(e.target.value.toLowerCase())}
                      maxLength={20}
                      className="input-field"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddFriend()}
                    />
                    <button
                      className="btn btn-accent"
                      onClick={() => handleAddFriend()}
                      disabled={loadingState.addFriend}
                    >
                      {loadingState.addFriend ? 'Adding...' : 'Add Friend'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'blocked' && (
              <div className="share-tab-content">
                {blockedList.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">✅</div>
                    <h3>No Blocked Users</h3>
                    <p>Your blocked list is empty</p>
                  </div>
                ) : (
                  <div className="blocked-list">
                    {blockedList.map(blocked => (
                      <div key={blocked.id} className="blocked-item">
                        <div className="blocked-avatar">
                          {blocked.handle[0]?.toUpperCase()}
                        </div>
                        <div className="blocked-info">
                          <h4>{blocked.handle}</h4>
                          <p className="blocked-date">Blocked {blocked.blockedDate}</p>
                        </div>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleUnblockFriend(blocked.id, blocked.handle)}
                          disabled={loadingState.unblockFriend === blocked.id}
                        >
                          {loadingState.unblockFriend === blocked.id ? '...' : 'Unblock'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareAccessCode;
